import type { Store } from 'vuex';
import type { Route } from 'vue-router';
import Router from 'vue-router';
import { magic } from '@gig/magic-sdk';
import { WalletViewRouteName } from '@gig/magic-wallet';

import '../../../components/responsible-gaming-modal';
import { getTimeOutHandling, TimeOutHandling } from '../../../util';
import { RouteName } from '../../../routes/routes.constants';
import { IAppState } from '../../../store/constants';
import { RESPONSIBLE_GAMING_NAMESPACE } from '../../../store/modules/responsible-gaming/responsible-gaming.constants';
import { responsibleGamingModalService } from '../../../components/responsible-gaming-modal/responsible-gaming-modal-service';

function isTimedOutAllowedRoute(to: Route): boolean {
    if (to.meta?.timedOutAllowed) {
        return true;
    }

    if (
        to.name === RouteName.WALLET &&
        to.params?.view === WalletViewRouteName.WITHDRAW
    ) {
        responsibleGamingModalService.close();
        return true;
    }

    if (to.name === RouteName.TRANSACTION_HISTORY) {
        responsibleGamingModalService.close();
        return true;
    }

    return false;
}

function getHomeRouteParams(to: Route): { language: string } | undefined {
    if (typeof to.params?.language === 'string') {
        return { language: to.params.language };
    }

    return undefined;
}

function redirectToHome(next: (to?: any) => void, to: Route) {
    openResponsibleGamingModal();

    return next({
        name: RouteName.HOME,
        params: getHomeRouteParams(to),
    });
}

function openResponsibleGamingModal() {
    responsibleGamingModalService.open();
}

export function initTimedOutGuard(router: Router, store: Store<IAppState>) {
    router.beforeEach(async (to, _, next) => {
        const user = magic.session.getUser();

        if (!user) {
            return next();
        }

        if (isTimedOutAllowedRoute(to)) {
            return next();
        }

        if (
            (await getTimeOutHandling()) !== TimeOutHandling.RESTRICTED_ACCESS
        ) {
            return next();
        }

        const rgState = store.state[RESPONSIBLE_GAMING_NAMESPACE];

        const resolved = rgState!.timeOutResolved;
        let isTimedOut = rgState!.isTimedOut;

        if (!resolved) {
            try {
                await store.dispatch(
                    `${RESPONSIBLE_GAMING_NAMESPACE}/loadUserBlocks`,
                );
                isTimedOut =
                    store.getters[`${RESPONSIBLE_GAMING_NAMESPACE}/isTimedOut`];
            } catch (error) {
                console.error('Error loading user blocks:', error);
                return next();
            }
        }

        if (!isTimedOut) {
            return next();
        }

        return redirectToHome(next, to);
    });
}
